
export abstract class LllNode {
  name!: string;
  fqn!: string;
  type!: "topic" | "activity-group" | "activity" | "subject" | "root";
  displayName!: string;
  description!: string;

  abstract getChildren(): LllNode[] | null;

  protected constructor(data: Record<string, any>) {
    Object.assign(this, data);
  }

  static createNode(data: Record<string, any>): LllNode {
    if (RootNode.isType(data)) {
      return new RootNode(data);
    } else if (TopicNode.isType(data)) {
      return new TopicNode(data);
    } else if (SubjectNode.isType(data)) {
      return new SubjectNode(data);
    } else if (ActivityGroupNode.isType(data)) {
      return new ActivityGroupNode(data);
    } else if (ActivityNode.isType(data)) {
      return new ActivityNode(data);
    }

    throw new Error(`unknown node type: ${data.type}`);
  }

  isTopic(): this is TopicNode {
    return this.type === 'topic';
  }
  isSubject(): this is SubjectNode {
    return this.type === 'subject';
  }
  isActivityGroup(): this is ActivityGroupNode {
    return this.type === 'activity-group';
  }
  isActivity(): this is ActivityNode {
    return this.type === 'activity';
  }

}

export class TopicNode extends LllNode {

  subTopics?: TopicNode[];
  activityGroups?: ActivityGroupNode[];

  constructor(data: Record<string, any>) {
    super(data);
    if (this.isType2(data) === false) {
      throw new Error("wrong node type. expected to be a TopicNode");
    }
    if (data.subTopics) {
      this.subTopics = data.subTopics
        // @ts-ignore
        .map(e => LllNode.createNode(e) as TopicNode);
    }
    if (data.activityGroups) {
      this.activityGroups = data.activityGroups
        // @ts-ignore
        .map(e => LllNode.createNode(e) as ActivityGroupNode);
    }
  }

  static isType(data: Record<string, any>): boolean {
    return data?.type === "topic";
  }

  isType2(data: Record<string, any>): boolean {
    return TopicNode.isType(data);
  }

  getChildren() {
    if(this.subTopics && this.subTopics.length > 0) {
      return this.subTopics;
    }
    if(this.activityGroups && this.activityGroups.length > 0) {
      return this.activityGroups;
    }
    return null;
  }
}

export class RootNode extends TopicNode {

  constructor(data: Record<string, any>) {
    super(data)
  }

  static isType(data: Record<string, any>): boolean {
    return data?.type === "root";
  }

  isType2(data: Record<string, any>): boolean {
    return RootNode.isType(data);
  }

}

export class SubjectNode extends TopicNode {
  subjectName!: string;

  constructor(data: Record<string, any>) {
    super(data)
    this.subjectName = data.subjectName;
  }

  static isType(data: Record<string, any>): boolean {
    return data?.type === "subject";
  }

  isType2(data: Record<string, any>): boolean {
    return SubjectNode.isType(data);
  }
}

export class ActivityGroupNode extends LllNode {

  activities?: ActivityNode[];


  constructor(data: Record<string, any>) {
    super(data);
    if (ActivityGroupNode.isType(data) === false) {
      throw new Error("wrong node type. expected to be a ActivityGroupNode");
    }
    if (data.activities) {
      this.activities = data.activities
        // @ts-ignore
        .map(e => LllNode.createNode(e) as ActivityNode)
        .filter( (e: ActivityNode)  => e.relativeLink);
    }
  }

  static isType(data: Record<string, any>): boolean {
    return data?.type === "activity-group";
  }

  getChildren() {
    return this.activities ?? null;
  }
}

export class ActivityNode extends LllNode {

  relativeLink!: string

  constructor(data: Record<string, any>) {
    super(data)
    if (ActivityNode.isType(data) === false) {
      throw new Error("wrong node type. expected to be a ActivityNode")
    }
    this.relativeLink = data.relativeLink;
  }

  static isType(data: Record<string, any>): boolean {
    return data?.type === "activity"
  }

  getChildren() {
    return null;
  }
}

interface TopicLike {
  name: string;
  fqn: string;
  displayName: string;
  description: string;
  subTopics: Topic[];
  activityGroups?: ActivityGroup[]
}

interface Topic extends TopicLike {
  type: "topic";
}

interface Subject extends TopicLike {
  type: "subject";
  subjectName: string;
}

interface ActivityGroup {
  mode: string;
  fqn: string;
  type: "activity-group";
  name: string;
  displayName: string;
  description: string;
  activities: Activity[];
}

interface Activity {
  type: "activity";
  relativeLink: string;
  name: string;
  displayName: string;
  description: string;
}

export const LllMetadata = {
  coinsPerActivity: 2
}